import {
  createFeatureHub,
  FeatureServiceProviderDefinition,
  FeatureServices,
  SharedFeatureService,
} from '@feature-hub/core';
import { defineLogger } from '@feature-hub/logger';
import { defineExternals, loadAmdModule } from '@feature-hub/module-loader-amd';
import { FeatureAppLoader, FeatureHubContextProvider } from '@feature-hub/react';
import { ApplicationRoot, IntegratorRoot, ThemeProvider } from '@volkswagen-onehub/components-core';
import * as React from 'react';
import { defineGfaLocalService, defineLocaleService } from '@volkswagen-onehub/locale-service';
import { Stage } from '@org-avp/avp-hck-dga-customerui-commons';
import { Brand, Market } from '@org-avp/models';
import { createRoot } from 'react-dom/client';
import { defineTrackingService } from '@volkswagen-onehub/tracking-service';
import { GetNavigationState } from '@volkswagen-onehub/page-info-service';
import { INTEGRATOR_CONSUMER_ID } from '@volkswagen-onehub/vw-starter-integrator-support';
import { defineSerializedStateManager } from '@feature-hub/serialized-state-manager';
import { createConsumerLocale } from './feature-services/create-consumer-locale';
import { externals } from './externals';
import { createConsumerLogger } from './feature-services/create-consumer-logger';
import {
  createTrackingServiceConfig,
  EnvironmentData,
} from './feature-services/create-consumer-tracking';
import { createPageInfoService } from './feature-services/create-consumer-pageInfo';
import {
  createFeatureAppService,
  featureAppConfigs,
} from './feature-services/create-feature-app-service';

import { defineServiceConfigEndpointProvider } from '@volkswagen-onehub/service-config-endpoint-provider';
import { layerManagerDefinition } from '@volkswagen-onehub/layer-manager';
import { createRootLocationTransformer, defineHistoryService } from '@feature-hub/history-service';
import {
  defineGfaNavigationService,
  defineNavigationService,
} from '@volkswagen-onehub/navigation-service';
import {
  defineDisclaimerManagerFactory,
  defineGfaDisclaimerManager,
} from '@volkswagen-onehub/disclaimer-manager';
import { faContentServiceDefinition } from '@volkswagen-onehub/fa-content-service';
import { defineApiKeyProvider } from '@volkswagen-onehub/api-key-provider';

const container = document.getElementById('root');
const root = createRoot(container!);
// Initial Tracking Data, will be overridden by Integrator Environment in AEM after deployment
const trackingData: EnvironmentData = {
  applicationId: 'vw-dga-integrator',
  countryCode: 'DE',
  language: 'de',
  currency: 'EUR',
  market: Market.DEU,
  brand: Brand.VW,
};

const getSecondaryConsumer = (path: string) => {
  const segments = path.split('/').filter(Boolean);
  const featureSegment = segments.find((segment) => segment.includes('digital-giveaway'));
  return featureSegment ? featureSegment.replace('.html', '') : segments[0];
};

const getNavigationState: GetNavigationState = () => {
  const secondaryConsumerId = getSecondaryConsumer(window.location.pathname);
  const currentCanonicalUrl = `/${secondaryConsumerId}.html`;
  const navigationState = {
    primaryConsumerId: INTEGRATOR_CONSUMER_ID,
    currentCanonicalUrl,
    subPage: false,
    secondaryConsumerId,
  };
  console.info(navigationState);
  return navigationState;
};

const stageFor: (hostname: string) => Stage = (hostname: string) => {
  if (!hostname || hostname === 'localhost') {
    return Stage.DEV;
  }

  if (hostname === 'volkswagen.dga.avp.tech') {
    return Stage.PROD;
  }

  return Stage.DEV;
};

const primaryConsumerHistoryKey = 'dealer-search';
const rootLocationTransformer = createRootLocationTransformer({
  consumerPathsQueryParamName: '---',
  primaryConsumerHistoryKey,
});

defineExternals(externals);

const { featureAppManager } = createFeatureHub(INTEGRATOR_CONSUMER_ID, {
  moduleLoader: loadAmdModule,
  featureServiceDefinitions: [
    defineLogger(createConsumerLogger),
    defineLocaleService(createConsumerLocale()),
    defineTrackingService(),
    createTrackingServiceConfig(trackingData),
    createPageInfoService(getNavigationState),
    defineSerializedStateManager(),
    createFeatureAppService(),
    defineGfaDisclaimerManager(),
    defineGfaLocalService(),
    defineGfaNavigationService(),
    defineHistoryService(rootLocationTransformer, { mode: 'browser' }),
    layerManagerDefinition,
    defineServiceConfigEndpointProvider({
      type: 'publish',
      content: 'onehub_pkw',
      country: 'de',
      language: 'de',
      envName: 'prod',
    }),
    defineNavigationService({
      primaryConsumerId: primaryConsumerHistoryKey,
      primaryConsumerLocation: () => {
        return { pathname: '/' };
      },
      pages: Object.fromEntries([]),
    }),
    defineDisclaimerManagerFactory({
      legalEntityLabels: { integrator: 'integrator' },
      defaultLegalEntity: 'integrator',
    }),
    faContentServiceDefinition,
    defineApiKeyProvider({
      apiKeysByConsumerId: Object.fromEntries(
        Object.values(featureAppConfigs).map((fa) => [fa.name, fa.apiKey || '']),
      ),
      apiKeysByConsumerName: Object.fromEntries(
        Object.values(featureAppConfigs).map((fa) => [fa.name, fa.apiKey || '']),
      ),
    }),
  ].filter((f) => !!f) as FeatureServiceProviderDefinition<SharedFeatureService, FeatureServices>[],
});

root.render(
  <FeatureHubContextProvider value={{ featureAppManager }}>
    <IntegratorRoot>
      <ApplicationRoot>
        <ThemeProvider>
          <FeatureAppLoader
            featureAppId="vw-avp-hck-dga-customerui"
            src="assets/feature-app/vw-avp-hck-dga-customerui.umd.js"
            config={{
              brand: Brand.VW,
              stage: stageFor(new URL(window.location.href).hostname),
            }}
          />
        </ThemeProvider>
      </ApplicationRoot>
    </IntegratorRoot>
  </FeatureHubContextProvider>,
);
